/**
 * Native image pick for a shot — the web half of Unity's `pickShotImage` / `shotImagePicked` pair
 * (Unity: Assets/Scripts/Timeline/ShotImagePickBridgeHandlers.cs).
 *
 * WHY A ROUND TRIP AND NOT <input type=file>. The board is the whole app on these hosts, and the
 * file input cannot give the owner's platform behaviour: the macOS Standalone WebView implements no
 * open-panel delegate (a file input there most likely shows nothing), and on iOS WebKit's own sheet
 * skips the app's Photos permission check and its denied message. So Unity runs the native picker
 * (macOS: the image document panel; iOS/iPadOS: the Photos permission check, then the album
 * picker) and hands the picked, already-scaled image back as a `file://` URL. The web turns that
 * URL into a `File` and runs the EXISTING drop pipeline, so the result is exactly "as if the image
 * had been dragged onto the Add shot slot" — no second apply path.
 *
 * ── The contract (both sides match it byte for byte) ─────────────────────────────────────────────
 *   web → Unity  {"action":"pickShotImage","requestId":"<uuid>","purpose":"add"|"replace","shotId":<int or -1>}
 *                purpose / shotId are for Unity's logs only; Unity NEVER writes the shot.
 *   Unity → web  {"type":"shotImagePicked","payload":{"requestId":"…",
 *                 "status":"picked"|"cancelled"|"failed"|"photos-denied"|"busy",
 *                 "imageUrl":"file://…?v=<ticks>"|null,"fileName":"…"|null}}
 *
 * ── What each status does here ──────────────────────────────────────────────────────────────────
 *   picked         fetch(imageUrl) → Blob → File (status 0 tolerated: file:// answers 0 with a
 *                  readable body, as sqliteBridge.ts documents)
 *   cancelled/busy null, silently — the user backed out, or a pick is already running natively
 *   failed         null + the `shotImagePickFailed` toast
 *   photos-denied  null + the web's own "photos denied" dialog (uiStore.photosDeniedDialogOpen).
 *                  Unity's ConfirmationPopupPanel would draw UNDER the board WebView, so the web,
 *                  which is the host here, shows it; its Settings button sends `openSettings`.
 *
 * ── One in flight, bounded ──────────────────────────────────────────────────────────────────────
 * A second call while one is pending resolves null at once (the first owns the native panel). The
 * wait is bounded by IMAGE_PICK_TIMEOUT_MS, deliberately ABOVE Unity's own 600 s picker watchdog:
 * Unity answers `failed` first when its watchdog fires, so the web timeout only ever catches a host
 * that never answers at all (and then reports it as a failure). Replies are matched by requestId,
 * so a late answer to a request that already timed out is dropped.
 *
 * ── The pending scrim ───────────────────────────────────────────────────────────────────────────
 * The macOS panel is NON-modal: the WebView behind it stays clickable. `uiStore.imagePickPending` is
 * up for the whole wait so the board can put a light scrim + "Choose an image…" over itself.
 *
 * uiStore and Toast are loaded LAZILY, for the reason features/timeline.ts records: uiStore
 * statically imports embeddedBridge, which imports this module — a static import here is a cycle
 * whose outcome depends on import order. Both writes go through ONE cached import promise, so the
 * "pending on" write can never land after the "pending off" one.
 */

import i18next from 'i18next';
import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';

// ─── Message types ───────────────────────────────────────────────────────

export type ShotImagePickStatus = 'picked' | 'cancelled' | 'failed' | 'photos-denied' | 'busy';
export type ShotImagePickPurpose = 'add' | 'replace';

export interface ShotImagePickedMessage {
  type: 'shotImagePicked';
  payload: {
    requestId: string;
    status: ShotImagePickStatus;
    /** A `file://…?v=<ticks>` URL on `picked`; bare JSON null on every other status. */
    imageUrl: string | null;
    /** The user's original file name when Unity knows it (iOS may, macOS does not); else null. */
    fileName: string | null;
  };
}

export type ImagePickMessage = ShotImagePickedMessage;

// ─── Module state ────────────────────────────────────────────────────────

/** Above Unity's 600 s picker watchdog on purpose — see the header. */
export const IMAGE_PICK_TIMEOUT_MS = 620_000;

/** The name a picked image gets when Unity sends no original file name. */
export const PICKED_IMAGE_FALLBACK_NAME = 'picked.png';

interface InFlight {
  requestId: string;
  timer: ReturnType<typeof setTimeout>;
  settle: (payload: ShotImagePickedMessage['payload']) => void;
}

let inFlight: InFlight | null = null;
let requestSeq = 0;

type UiStoreModule = typeof import('@/store/uiStore.ts');
let uiModule: Promise<UiStoreModule> | null = null;
/** ONE cached import promise: every write chains on it, so they land in call order. */
function withUi(fn: (m: UiStoreModule) => void): void {
  uiModule ??= import('@/store/uiStore.ts');
  void uiModule.then(fn).catch((e) => console.warn('[imagePick] uiStore write failed', e));
}

function setPending(value: boolean): void {
  withUi(({ useUiStore }) => useUiStore.getState().setImagePickPending(value));
}

function showFailedToast(): void {
  // Dynamic import keeps Toast (a React component module) out of the bridge's eager graph.
  void import('@/components/Toast.tsx')
    .then(({ showToast }) => showToast(i18next.t('common:shotImagePickFailed'), 'error'))
    .catch((e) => console.warn('[imagePick] failure toast could not be shown', e));
}

function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  requestSeq += 1;
  return `imagePick-${Date.now().toString(36)}-${requestSeq}`;
}

/** Fetch Unity's staged file into a File. Null when the file cannot be read. */
async function fetchPickedFile(imageUrl: string, fileName: string | null): Promise<File | null> {
  try {
    const response = await fetch(imageUrl);
    // file:// URLs answer status 0 with ok=false — the body is still readable (sqliteBridge.ts).
    if (!response.ok && response.status !== 0) return null;
    const blob = await response.blob();
    if (blob.size === 0) return null;
    // Unity stages a PNG; keep a more specific image type if the host reports one.
    const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png';
    const name = typeof fileName === 'string' && fileName.length > 0 ? fileName : PICKED_IMAGE_FALLBACK_NAME;
    return new File([blob], name, { type });
  } catch {
    return null;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────

/** Is a pick waiting on the native picker right now? */
export function isImagePickInFlight(): boolean {
  return inFlight !== null;
}

/**
 * Ask Unity for an image through the platform's native picker and resolve it as a `File`, or null
 * when there is nothing to use (cancelled, busy, denied, failed, timed out, or another pick is
 * already pending). Never rejects. The failure toast and the denied dialog are shown HERE, so a
 * caller only ever has to handle "a File" or "nothing".
 *
 * @param purpose 'add' (the Add shot chooser) or 'replace' (an existing shot) — for Unity's logs.
 * @param shotId  the target shot for 'replace', -1 for 'add' — for Unity's logs.
 */
export function pickImageFile(purpose: ShotImagePickPurpose, shotId: number): Promise<File | null> {
  if (inFlight !== null) return Promise.resolve(null);
  const requestId = newRequestId();
  return new Promise<File | null>((resolve) => {
    const settle = (payload: ShotImagePickedMessage['payload']): void => {
      if (inFlight === null || inFlight.requestId !== requestId) return;
      clearTimeout(inFlight.timer);
      inFlight = null;
      const finish = (file: File | null): void => {
        setPending(false);
        resolve(file);
      };
      switch (payload.status) {
        case 'picked': {
          if (typeof payload.imageUrl !== 'string' || payload.imageUrl === '') {
            showFailedToast();
            finish(null);
            return;
          }
          void fetchPickedFile(payload.imageUrl, payload.fileName).then((file) => {
            if (file === null) showFailedToast();
            finish(file);
          });
          return;
        }
        case 'cancelled':
        case 'busy':
          finish(null);
          return;
        case 'photos-denied':
          withUi(({ useUiStore }) => useUiStore.getState().setPhotosDeniedDialogOpen(true));
          finish(null);
          return;
        case 'failed':
        default:
          // An unknown status is a contract break; report it the way a failure is reported rather
          // than leaving the user with no answer at all.
          showFailedToast();
          finish(null);
      }
    };
    const timer = setTimeout(() => {
      settle({ requestId, status: 'failed', imageUrl: null, fileName: null });
    }, IMAGE_PICK_TIMEOUT_MS);
    inFlight = { requestId, timer, settle };
    setPending(true);
    // Payload fields are TOP-LEVEL beside `action`, as every board verb sends them.
    enqueueSendToNative('pickShotImage', { requestId, purpose, shotId });
  });
}

// ─── Inbound handler ─────────────────────────────────────────────────────

export const imagePickHandlers = {
  shotImagePicked: (msg: ShotImagePickedMessage) => {
    const payload = msg.payload;
    if (!payload || typeof payload.requestId !== 'string') return;
    // One filter covers a reply nobody asked for, a duplicate, and the answer to a timed-out ask.
    if (inFlight === null || inFlight.requestId !== payload.requestId) return;
    inFlight.settle({
      requestId: payload.requestId,
      status: payload.status,
      imageUrl: payload.imageUrl ?? null,
      fileName: payload.fileName ?? null,
    });
  },
} satisfies HandlerMap;

/** Test-only reset — module state is process-wide, so suites would leak into each other. */
export function __resetImagePickForTests(): void {
  if (inFlight !== null) clearTimeout(inFlight.timer);
  inFlight = null;
  requestSeq = 0;
}
